import { Box, IconButton, Text, Tooltip, type ButtonProps } from "@chakra-ui/react"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { IconEdit, IconTrash } from "@tabler/icons-react"
import { FMPTableType } from "../../../common/commonConstants"
import { Colors } from "../../../theme/colors"
import { DeleteProjectFromFMP } from "../editIndividualRow/fmp.deleteproject"
import { CENTERED_COLUMN_KEYS, type ViewColumnsInterface } from "./fmpProjectTable"

const ROW_ICON_SIZE = 20
const rowIconButtonStyle = (isDanger?: boolean): ButtonProps => ({
  variant: "unstyled",
  minW: "28px",
  w: "28px",
  h: "28px",
  minH: "28px",
  borderRadius: "6px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bg: "transparent",
  color: "#4A4A4A",
  _hover: isDanger ? { bg: Colors.Errors[100], color: Colors.Errors.error } : { bg: Colors.Surface.s1, color: Colors.Secondary.c2 }
})

interface FMPProjectRowItemProps {
  columns: ViewColumnsInterface[]
  tableType?: FMPTableType
  index: number
  editOrDeleteCallback: (type: "edit" | "delete", id: string) => void
  onDeleteSuccessCallBack: () => void
  row: any
  fmpId: number
  templateColumns: string
  columnCount: number
  isReorderMode?: boolean
  isRowOutOfOrder?: boolean
}

export const FMPProjectRowItem: React.FC<FMPProjectRowItemProps> = ({
  columns,
  tableType,
  index,
  editOrDeleteCallback,
  row,
  fmpId,
  onDeleteSuccessCallBack,
  templateColumns,
  columnCount,
  isReorderMode = false,
  isRowOutOfOrder = false
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: row.id })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    backgroundColor: transform?.x ? Colors.Secondary.c2_100 : "transparent"
  }

  // While reorder mode is active, the "No" column is highlighted green for
  // rows still at their last-saved position and amber once moved out of it.
  // The column header keeps its normal table-header styling regardless.
  const rowNumberBg = isReorderMode ? (isRowOutOfOrder ? "#D97706" : "#008000") : undefined
  // Drop the grey grid-line border on that cell while highlighted so the
  // fill runs edge-to-edge instead of being muted by a competing outline.
  const rowNumberBorder = isReorderMode ? "none" : `1px solid ${Colors.Secondary.c2_500}`

  return (
    <Box
      display="grid"
      gridColumn={`1 / span ${columnCount}`}
      w="100%"
      gridTemplateColumns={templateColumns}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
    >
      {columns.map(({ dataKey }, index) => (
        <Box
          key={dataKey}
          color={index === 0 && isReorderMode ? "white" : Colors.Secondary.c2}
          p={index === 0 ? "0" : 2}
          borderRight={index === 0 ? rowNumberBorder : `1px solid ${Colors.Secondary.c2_500}`}
          alignItems="center"
          borderBottom={index === 0 ? rowNumberBorder : `1px solid ${Colors.Secondary.c2_500}`}
          display="flex"
          textTransform="capitalize"
          wordBreak="normal"
          overflowWrap="break-word"
          whiteSpace="normal"
          {...(CENTERED_COLUMN_KEYS.includes(dataKey) && { justifyContent: "center", textAlign: "center" })}
          {...(index === 0 && { borderLeft: rowNumberBorder })}
          {...(index === 0 && { alignItems: "center", justifyContent: "center" })}
          {...(index === 0 && { bg: rowNumberBg })}
        >
          {dataKey === "fmp_product_services" && (tableType === FMPTableType.VIEW_FMP || tableType === FMPTableType.Edit_FMP) ? (
            <Box display="flex" flexDirection="column" gap="4px" w="100%">
              <Text variant="RCaption">{row[dataKey] ?? ""}</Text>
              <Text variant="RCaption" borderTop="1px solid #e0e0e0" pt="3px">
                Fitting allowed: {row.fitting_allowed || "—"}
              </Text>
            </Box>
          ) : (
            dataKey && <Text variant="RCaption">{row[dataKey.toString()] ?? ""}</Text>
          )}
        </Box>
      ))}

      {tableType === FMPTableType.Edit_FMP && (
        <>
          <Box
            display="flex"
            color={Colors.Secondary.c2}
            p={index === 0 ? "0" : 2}
            borderBottom={`1px solid ${Colors.Secondary.c2_500}`}
            justifyContent="center"
            wordBreak="normal"
            overflowWrap="break-word"
            whiteSpace="normal"
            borderRight={`1px solid ${Colors.Secondary.c2_500}`}
            alignItems="center"
          >
            <Tooltip label="Edit" hasArrow fontSize="11px" placement="top">
              <IconButton
                aria-label="Edit"
                icon={<IconEdit size={ROW_ICON_SIZE} stroke={1.75} />}
                onPointerDown={e => {
                  e.stopPropagation()
                }}
                onMouseDown={e => {
                  e.stopPropagation()
                }}
                onTouchStart={e => {
                  e.stopPropagation()
                }}
                onClick={e => {
                  e.stopPropagation()
                  editOrDeleteCallback("edit", row.id)
                }}
                {...rowIconButtonStyle()}
              />
            </Tooltip>
          </Box>
          <Box
            display="flex"
            color={Colors.Secondary.c2}
            p={index === 0 ? "0" : 2}
            borderRight={`1px solid ${Colors.Secondary.c2_500}`}
            borderBottom={`1px solid ${Colors.Secondary.c2_500}`}
            justifyContent="center"
            wordBreak="normal"
            overflowWrap="break-word"
            whiteSpace="normal"
            alignItems="center"
          >
            <Tooltip label="Delete" hasArrow fontSize="11px" placement="top">
              <Box as="span" display="inline-block">
                <DeleteProjectFromFMP
                  fmpId={fmpId}
                  projectId={row.system_id}
                  onDeleteSuccess={onDeleteSuccessCallBack}
                  deleteButtonProps={{
                    ...rowIconButtonStyle(true),
                    onPointerDown: e => {
                      e.stopPropagation()
                    },
                    onMouseDown: e => {
                      e.stopPropagation()
                    },
                    onTouchStart: e => {
                      e.stopPropagation()
                    }
                  }}
                >
                  <IconTrash size={ROW_ICON_SIZE} stroke={1.75} />
                </DeleteProjectFromFMP>
              </Box>
            </Tooltip>
          </Box>
        </>
      )}
    </Box>
  )
}
